import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import mergeClasses, { cn } from '@/shared/lib/utils';
const tableVariants = cva('w-full caption-bottom text-sm', {
  variants: {
    variant: {
      default: '',
      list: 'border-separate border-spacing-y-2'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement> & VariantProps<typeof tableVariants> & {
  divClassname?: string;
}>(({
  className,
  divClassname,
  variant,
  ...props
}, ref) => <div className={mergeClasses('relative w-full', divClassname)}>
    <table ref={ref} className={mergeClasses(tableVariants({
    variant
  }), className)} {...props} />
  </div>);
Table.displayName = 'Table';
const tableHeaderVariants = cva('', {
  variants: {
    variant: {
      default: 'sticky top-0 rounded-t-md [&_tr]:border-b [&_tr]:border-background [&_tr]:bg-accent-dark [&_tr]:hover:bg-accent-dark/80',
      list: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableHeaderVariants>>(({
  className,
  variant,
  ...props
}, ref) => <thead ref={ref} className={mergeClasses(tableHeaderVariants({
  variant
}), className)} {...props} />);
TableHeader.displayName = 'TableHeader';
const tableBodyVariants = cva('[&_tr]:bg-accent', {
  variants: {
    variant: {
      default: '[&_tr:last-child]:border-0',
      list: '[&_tr]:h-[50px] [&_tr_td:first-child]:rounded-l-lg [&_tr_td:last-child]:rounded-r-lg'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableBodyVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tbody ref={ref} className={mergeClasses(tableBodyVariants({
  variant,
  className
}))} {...props} />);
TableBody.displayName = 'TableBody';
const tableFooterVariants = cva('', {
  variants: {
    variant: {
      default: 'sticky bottom-0 border-t border-background bg-muted/50 font-medium [&>tr]:last:border-b-0 [&_tr]:bg-accent-dark [&_tr]:hover:bg-accent-dark/80',
      list: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableFooterVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tfoot ref={ref} className={mergeClasses(tableFooterVariants({
  variant,
  className
}))} {...props} />);
TableFooter.displayName = 'TableFooter';
const tableRowVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b border-background transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      list: 'border-0 '
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement> & VariantProps<typeof tableRowVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tr ref={ref} className={mergeClasses(tableRowVariants({
  variant,
  className
}))} {...props} />);
TableRow.displayName = 'TableRow';
const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(({
  className,
  ...props
}, ref) => <th ref={ref} className={cn('h-8 border-none px-3 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0', className)} {...props} />);
TableHead.displayName = 'TableHead';
const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(({
  className,
  ...props
}, ref) => <td ref={ref} className={cn('p-3 align-middle [&:has([role=checkbox])]:pr-0', className)} {...props} />);
TableCell.displayName = 'TableCell';
const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(({
  className,
  ...props
}, ref) => <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />);
TableCaption.displayName = 'TableCaption';
export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };